import {
  closestPluginWordMatch,
  compareTwoStringsInsensitive,
  findCharNewKeyPressed,
  findNearestSlashIndex,
  splitPluginTextInputMessage,
  checkValidUrl,
  containsTrustedMarkitEventLink,
} from "@markit/common.utils";
import { Colors } from "../../../../utils/colors";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import {
  Event,
  ShortenedPluginKeywords,
  TextPluginOption,
} from "@markit/common.types";
import { useCallback, useMemo, useRef, useState } from "react";
import { TextareaAutosize } from "@mui/material";
import getCaretCoordinates from "textarea-caret";
import { useOnMount } from "../../../../hooks/useOnMount";
import { getVisibleTextPluginOptions } from "../../../../utils/textingUtils";
import { MassTextMediaAttachment } from "./MassTextMediaAttachment";

type InsertPluginTextInputProps = {
  message: string;
  setMessage: (message: string) => void;
  placeholder: string;
  cursorPosition: number;
  setCursorPosition: (cursorPosition: number) => void;
  pluginWords: Set<string>;
  campaignId: string;
  event: Event | undefined;
  image: string;
  removeImage: () => void;
  fixedBottomText?: JSX.Element | undefined;
  maxLength?: number;
  disablePlugins?: boolean;
  trustedSender: boolean;
};

export const InsertPluginTextInput = (props: InsertPluginTextInputProps) => {
  const {
    message,
    setMessage,
    placeholder,
    cursorPosition,
    setCursorPosition,
    pluginWords,
    campaignId,
    event,
    image,
    removeImage,
    fixedBottomText,
    maxLength,
    disablePlugins,
    trustedSender,
  } = props;
  const [showPluginPopup, setShowPluginPopup] = useState(false);
  const [searchedPluginWord, setSearchedPluginWord] = useState("");
  const [hoverIndex, setHoverIndex] = useState(-1);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const caretPosition = useRef({ top: 0, left: 0, height: 0 });

  useOnMount(() => {
    const textarea = textAreaRef.current;
    if (!textarea) return;
    const handleInput = () => {
      const position = getCaretCoordinates(textarea, textarea.selectionEnd);
      caretPosition.current = position;
    };
    textarea.addEventListener("input", handleInput);
    return () => textarea.removeEventListener("input", handleInput);
  });

  useOnMount(() => {
    const input = document.getElementById("masstextmessage");
    if (!input) return;
    const handler = (e: MouseEvent) =>
      setCursorPosition((e.target as HTMLTextAreaElement).selectionStart ?? 0);
    input.addEventListener("click", handler);
    return () => input.removeEventListener("click", handler);
  });

  const styles = {
    textInputText: { fontSize: 14, fontWeight: "400", letterSpacing: "normal" },
    textInputShared: {
      fontSize: "14px",
      lineHeight: "1.25",
      fontFamily: "Arial, sans-serif",
      padding: 0,
      margin: 0,
      whiteSpace: "pre-wrap",
      letterSpacing: "normal",
      width: "100%",
      paddingTop: 10,
    },
    pluginSubtext: {
      color: Colors.GRAY2,
      fontWeight: "500",
      fontSize: 12,
    },
    pluginPopupListMaxHeight: 160,
  };

  const visibleTextPluginOptions = useMemo(
    () =>
      disablePlugins ? [] : getVisibleTextPluginOptions(event, campaignId),
    [disablePlugins, event, campaignId]
  );

  const textPluginOptionsToShow = useMemo(() => {
    if (searchedPluginWord !== "") {
      return visibleTextPluginOptions.filter((plugin) =>
        plugin.name.toLowerCase().includes(searchedPluginWord.toLowerCase())
      );
    }
    return visibleTextPluginOptions;
  }, [searchedPluginWord, visibleTextPluginOptions]);

  const showTextPluginPopup = useMemo(
    () => showPluginPopup && textPluginOptionsToShow.length > 0,
    [showPluginPopup, textPluginOptionsToShow.length]
  );

  // Checks to see if the cursor is currently positioned within the substring in focus and if that substring is a valid plugin starter
  // Needed for checking if the pluginPopup should be visible when typing plugins with multiple words
  const cursorWithinFocusedPlugin = useCallback(
    (substring: string, newMessage: string) => {
      const startsWithSlash = /\//g;
      const matches = newMessage.match(startsWithSlash);
      if (matches) {
        let index = 0;
        for (let i = 0; i < matches.length; i++) {
          const foundSlashIndex = newMessage.indexOf(substring, index);
          if (foundSlashIndex !== -1) {
            const endIndex =
              foundSlashIndex +
              substring.length +
              (ShortenedPluginKeywords.some((word) =>
                substring.includes(word.slice(1).toLowerCase().trim())
              )
                ? 1
                : 0);
            if (
              cursorPosition >= foundSlashIndex &&
              cursorPosition <= endIndex
            ) {
              return true;
            } else {
              index = foundSlashIndex + 1;
            }
          } else {
            index = foundSlashIndex + 1;
          }
        }
      }
      return false;
    },
    [cursorPosition]
  );

  // Checks to see if the substring in the message matches the format of any plugin word
  const doesSubstringMatchPlugin = useCallback(
    (substring: string, newMessage: string) => {
      if (!substring.startsWith("/")) {
        return false;
      }
      if (!cursorWithinFocusedPlugin(substring, newMessage)) {
        return false;
      }

      // Find plugin that matches the substring based on letter matching
      const foundPluginWord = closestPluginWordMatch(
        substring,
        visibleTextPluginOptions
      );
      if (foundPluginWord === "") {
        return false;
      }
      let substringIndex = 0;
      for (const char of substring) {
        if (
          compareTwoStringsInsensitive(char, foundPluginWord[substringIndex])
        ) {
          substringIndex++;
        } else {
          substringIndex = 0; // Reset the index if characters don't match
        }

        if (substringIndex === foundPluginWord.length) {
          break;
        }
      }
      if (substringIndex === substring.length) {
        setSearchedPluginWord(substring);
        return true;
      }
      return false;
    },
    [cursorWithinFocusedPlugin, visibleTextPluginOptions]
  );

  // Called within onKeyPress to determine whether to show or hide the plugin popup modal
  const handleChangedText = useCallback(
    (inputText: string) => {
      setMessage(inputText);
      const words = splitPluginTextInputMessage(inputText);

      if (words.length === 0 || !words.some((word) => word.includes("/"))) {
        setShowPluginPopup(false);
      }

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        if (word.startsWith("/")) {
          const foundPluginWord = closestPluginWordMatch(
            word,
            visibleTextPluginOptions
          );
          if (
            compareTwoStringsInsensitive(word, foundPluginWord) &&
            foundPluginWord !== ""
          ) {
            // if the word becomes the same as the plugin name, dismiss popup
            const replaced = inputText.replace(word, foundPluginWord);
            setMessage(replaced);
            setShowPluginPopup(false);
          } else if (doesSubstringMatchPlugin(word.trim(), inputText)) {
            // if any word starts with a forward slash and matches part of plugin, show popup
            setShowPluginPopup(true);
            return;
          }
        } else {
          setSearchedPluginWord("");
          setShowPluginPopup(false);
        }
      }
    },
    [doesSubstringMatchPlugin, setMessage, visibleTextPluginOptions]
  );

  // Called within onKeyPress to determine whether to show or hide the plugin popup modal
  const addPluginWordOnPress = useCallback(
    (pluginOption: TextPluginOption) => {
      const startsWithSlash = /\//g;
      const matches = message.match(startsWithSlash);
      if (matches) {
        let index = 0;
        for (let i = 0; i < matches.length; i++) {
          const foundWord = matches[i];
          const foundSlashIndex = message.indexOf(foundWord, index);
          if (foundSlashIndex !== -1) {
            const nearestSlashIndex = findNearestSlashIndex(
              message,
              cursorPosition
            );
            if (foundSlashIndex === nearestSlashIndex) {
              const slashWord = message.substring(
                foundSlashIndex,
                cursorPosition
              );
              const newMessage =
                message.substring(0, foundSlashIndex) +
                pluginOption.name +
                " " +
                message.substring(foundSlashIndex + slashWord.length);
              const newCursorPosition =
                foundSlashIndex + pluginOption.name.length + 1;
              setMessage(newMessage);
              setCursorPosition(newCursorPosition);
              requestAnimationFrame(() => {
                if (textAreaRef.current) {
                  textAreaRef.current.selectionStart = newCursorPosition;
                  textAreaRef.current.selectionEnd = newCursorPosition;
                }
              });
            } else {
              index = foundSlashIndex + 1;
            }
          }
        }
      }
      setShowPluginPopup(false);
    },
    [cursorPosition, message, setCursorPosition, setMessage]
  );

  // Called on every single key press that the user inputs and determines what to do with the key
  // selectionAfterChange: textarea selectionEnd after the DOM applied the edit (React cursorPosition can
  // still be the initial message.length until onChange runs, so Backspace must not use stale cursor for
  // whole-plugin delete).
  const handleKeyPress = useCallback(
    (newMassTextMessage: string, selectionAfterChange: number) => {
      const key = findCharNewKeyPressed(newMassTextMessage, message);
      if (key === "Backspace") {
        const charsRemoved = Math.max(
          0,
          message.length - newMassTextMessage.length
        );
        const cursorBeforeBackspace =
          charsRemoved > 0
            ? selectionAfterChange + charsRemoved
            : cursorPosition;

        // check if backspace is at the end of a plugin word
        const pluginWordsArray = Array.from(pluginWords);
        for (const pluginWord of pluginWordsArray) {
          if (
            cursorBeforeBackspace >= pluginWord.length &&
            message.substring(
              cursorBeforeBackspace - pluginWord.length,
              cursorBeforeBackspace
            ) === pluginWord
          ) {
            const tailStart = cursorBeforeBackspace - charsRemoved;
            const updatedMassTextMessage =
              newMassTextMessage.substring(
                0,
                cursorBeforeBackspace - pluginWord.length
              ) + newMassTextMessage.substring(tailStart);
            const updatedCursorPosition =
              cursorBeforeBackspace - pluginWord.length;
            setMessage(updatedMassTextMessage);
            setCursorPosition(updatedCursorPosition);
            // Keep caret at deletion point after controlled value update.
            requestAnimationFrame(() => {
              if (textAreaRef.current) {
                textAreaRef.current.selectionStart = updatedCursorPosition;
                textAreaRef.current.selectionEnd = updatedCursorPosition;
              }
            });
            return;
          }
        }

        handleChangedText(newMassTextMessage);
      } else if (key === "Enter" && showTextPluginPopup) {
        addPluginWordOnPress(textPluginOptionsToShow[0]);
      } else {
        handleChangedText(newMassTextMessage);
      }
    },
    [
      addPluginWordOnPress,
      cursorPosition,
      handleChangedText,
      message,
      pluginWords,
      setCursorPosition,
      setMessage,
      showTextPluginPopup,
      textPluginOptionsToShow,
    ]
  );

  return (
    <div className="ColumnNormal" style={{ position: "relative" }}>
      <div className="ColumnNormal" style={{ width: "100%", zIndex: 0 }}>
        <TextareaAutosize
          id="masstextmessage"
          key="masstextstextarea"
          ref={textAreaRef}
          minRows={image || fixedBottomText ? 2 : 10}
          value={message}
          placeholder={placeholder}
          onChange={(e) => {
            const newCursorPosition = e.target.selectionEnd;
            if (cursorPosition !== newCursorPosition) {
              setCursorPosition(newCursorPosition);
            }
            handleKeyPress(e.target.value, newCursorPosition);
          }}
          className="MassTextsTextArea"
          style={{
            zIndex: 1,
            color: Colors.TRANSPARENT,
            border: "none",
            overflow: "hidden",
            outline: "none",
            resize: "none",
            height: "auto",
            ...styles.textInputShared,
          }}
          maxLength={maxLength ?? 9999}
        />
        <div
          style={{
            zIndex: 0,
            position: "absolute",
            top: 0,
            left: 0,
            wordBreak: "break-word",
            ...styles.textInputShared,
          }}
        >
          {splitPluginTextInputMessage(message).map((word, index) => {
            const pluginWord = pluginWords.has(word.trim());
            const isMarkitEventLink = containsTrustedMarkitEventLink(
              word.trim(),
              trustedSender
            );
            const isExternalLink = checkValidUrl(word.trim());
            return (
              <span
                key={index}
                style={{
                  color:
                    pluginWord || isMarkitEventLink
                      ? Colors.GREEN2
                      : isExternalLink
                        ? Colors.BLUE5
                        : Colors.BLACK,
                }}
              >
                {word}
              </span>
            );
          })}
        </div>
      </div>
      {showTextPluginPopup ? (
        <AnimatePresence>
          <LazyMotion features={domAnimation}>
            <m.div
              className="ColumnNormal"
              style={{
                zIndex: 99,
                position: "absolute",
                backgroundColor: Colors.WHITE,
                boxShadow: "0px 4px 24px 5px #1E1E371A",
                padding: 7,
                borderRadius: 8,
                width: 280,
                left: 50,
                top: 0,
                marginTop: caretPosition.current.top + 20,
              }}
              initial={{ top: "0px", opacity: 0 }}
              animate={{ top: "10px", opacity: 1 }}
              exit={{ top: "0px", opacity: 0 }}
              transition={{ duration: 0.3, type: "tween" }}
            >
              <span
                style={{
                  ...styles.pluginSubtext,
                  paddingInline: 7,
                  paddingBottom: 7,
                }}
              >
                Insert Plugin
              </span>
              <div
                className="ColumnNormal"
                style={{
                  maxHeight: styles.pluginPopupListMaxHeight,
                  overflowY: "scroll",
                  overflowX: "hidden",
                }}
              >
                {textPluginOptionsToShow.map((pluginOption, index) => (
                  <div
                    key={`${pluginOption.name}-${index}`}
                    className="ColumnNormalSelect"
                    style={{
                      padding: 7,
                      borderRadius: 8,
                      backgroundColor:
                        index === hoverIndex ? Colors.GRAY6 : Colors.WHITE,
                    }}
                    onMouseEnter={() => setHoverIndex(index)}
                    onClick={() => addPluginWordOnPress(pluginOption)}
                  >
                    <span style={styles.textInputText}>
                      {pluginOption.name}
                    </span>
                    <span
                      style={{
                        ...styles.pluginSubtext,
                        marginTop: 3,
                      }}
                    >
                      {pluginOption.description}
                    </span>
                  </div>
                ))}
              </div>
            </m.div>
          </LazyMotion>
        </AnimatePresence>
      ) : null}
      {fixedBottomText ? (
        <div
          style={{
            zIndex: 0,
            paddingTop: 24,
            paddingBottom: image ? 24 : 120,
          }}
        >
          {fixedBottomText}
        </div>
      ) : null}
      {image ? (
        <MassTextMediaAttachment mediaUrl={image} removeImage={removeImage} />
      ) : null}
    </div>
  );
};
